import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { convertIstDateTimeStringToUtc, isIsoDateTimeWithZone, isIsoDateTimeWithoutZone, normalizeUtcDateTimeString } from '../utils/date-time.util';

@Injectable()
export class TimezoneInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const normalizedRequest = this.normalizeRequest(request);
    return next.handle(normalizedRequest).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          return event.clone({ body: this.normalizeResponseBody(event.body) });
        }
        return event;
      })
    );
  }

  private normalizeRequest(request: HttpRequest<any>): HttpRequest<any> {
    if (!this.canTransform(request.body)) {
      return request;
    }
    return request.clone({
      body: this.transformRequestBody(request.body)
    });
  }

  private normalizeResponseBody(body: any): any {
    if (!this.canTransform(body)) {
      return body;
    }
    return this.transformResponseBody(body);
  }

  private transformRequestBody(value: any): any {
    if (Array.isArray(value)) {
      return value.map((item: any) => this.transformRequestBody(item));
    }
    if (this.isPlainObject(value)) {
      const result: any = {};
      Object.keys(value).forEach((key: string) => {
        result[key] = this.transformRequestBody(value[key]);
      });
      return result;
    }
    if (isIsoDateTimeWithoutZone(value) && !isIsoDateTimeWithZone(value)) {
      return convertIstDateTimeStringToUtc(value);
    }
    return value;
  }

  private transformResponseBody(value: any): any {
    if (Array.isArray(value)) {
      return value.map((item: any) => this.transformResponseBody(item));
    }
    if (this.isPlainObject(value)) {
      const result: any = {};
      Object.keys(value).forEach((key: string) => {
        result[key] = this.transformResponseBody(value[key]);
      });
      return result;
    }
    if (isIsoDateTimeWithoutZone(value) && !isIsoDateTimeWithZone(value)) {
      return normalizeUtcDateTimeString(value);
    }
    return value;
  }

  private canTransform(value: any): boolean {
    return !!value
      && !(value instanceof FormData)
      && !(value instanceof Blob)
      && !(value instanceof ArrayBuffer);
  }

  private isPlainObject(value: any): boolean {
    return value !== null && typeof value === 'object' && value.constructor === Object;
  }
}
